import { FieldValues, get, Path, useFormContext } from 'react-hook-form';
import { FormComponentProps } from '../types';
import React, { useMemo } from 'react';
import { ConvertStringToHtmlId } from '../../libs/ts/ConvertStringToHtmlId';

interface FormFileInputProps<T> extends FormComponentProps<T> {
  title: string;
  mandatory?: boolean;
  className?: string;
  type?: string;
  hideErrorControl?: boolean;
  disabled?: boolean;
}

export const FormFileInput = <T extends FieldValues>(props: FormFileInputProps<T>) => {
  const {
    register,
    watch,
    formState: { errors }
  } = useFormContext<T>();

  const errorForName = React.useMemo(() => {
    return get(errors, props.name);
  }, [errors, props.name]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const inputProps = register<Path<T>>(props.name);

  const backgroundColour = useMemo(() => {
    if (props.readonly) {
      return 'bg-transparent';
    } else if (errorForName) {
      return 'bg-feedback-dark-red';
    } else {
      return 'bg-interactive-bold-green-db-12';
    }
  }, [errorForName, props.readonly]);

  const borderColour = () => {
    if (props.readonly) {
      return 'border-transparent';
    } else if (errorForName) {
      return 'border-b-feedback-red';
    } else {
      return 'border-b-solid-bold-green';
    }
  };

  const labelTextColour = () => {
    if (props.readonly) {
      return 'text-solid-white';
    } else if (errorForName) {
      return 'text-feedback-red';
    } else {
      return 'text-solid-bold-green';
    }
  };

  const errorControl = () => {
    const error = errorForName;
    if (!error) {
      return <br />;
    }
    return <label className="text-feedback-red text-xs">{error.message?.toString()}</label>;
  };

  const fileList: FileList = watch(props.name);

  const fileName = useMemo(() => {
    if (!fileList || fileList.length === 0) {
      return null;
    }
    return fileList[0].name;
  }, [fileList]);

  return (
    <div className={`w-full ${props.className}`}>
      <label
        htmlFor={`file-input-${ConvertStringToHtmlId(props.title)}`}
        className={`${backgroundColour} ${borderColour()} pl-3 border-b-2 pt-2 pb-2 text-solid-white flex flex-col w-full cursor-pointer`}
      >
        <div className="flex flex-row justify-between pr-3">
          <p className={`cursor-text text-xs ${labelTextColour()} block`}>{props.title}</p>
          {props.mandatory && !props.readonly && <p className={`text-xs ${labelTextColour()} block`}>*</p>}
        </div>
        <div className="flex flex-row h-6">
          <p className={`truncate ${!fileName ? 'text-secondary-deep-blue-30' : 'text-solid-white'}`}>{!fileName ? 'No File Chosen' : fileName}</p>
        </div>
        <input {...inputProps} id={`file-input-${ConvertStringToHtmlId(props.title)}`} className={`hidden focus:outline-none w-full`} type="file" disabled={props.disabled} />
      </label>
      {!props.hideErrorControl && <div>{errorControl()}</div>}
    </div>
  );
};
